import {
  Alert,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useState } from "react";
import { PlayerDirectory } from "../../../components/player/PlayerAvatar";
import { useLeague } from "../../../hooks/useLeague/useLeague";
import { Game, GameMetadata } from "../types/game";
import { TeamRecords } from "../types/records";
import { Rules } from "../types/rules";
import { Team } from "../types/team";
import { TeamPollRank } from "../types/poll";
import MatchupCard, {
  MatchupCardCompletionSource,
  MatchupCardExpansionMode,
  MatchupCardProps,
  MatchupCardState,
} from "./MatchupCard";

interface GameCardProps {
  game: Game;
  metadata?: GameMetadata;
  homeTeam: Team;
  awayTeam: Team;
  leagueId?: string;
  year?: number;
  week?: number;
  onMetadataChange?: (update: {
    id: string | number;
    line?: number;
    flavorText?: string;
  }) => void;
  currentUserAvatar?: { initials: string; url?: string; side: "home" | "away" };
  onClick?: () => void;
  onEdit?: () => void;
  isSelected?: boolean;
  isGameOfTheWeek?: boolean;
  onGameOfTheWeekClick?: () => void;
  showGameOfTheWeekButton?: boolean;
  pickedTeamId?: number;
  onPick?: (teamId: number) => void;
  isLocked?: boolean;
  onToggleLock?: () => void;
  canLock?: boolean;
  lockCount?: number;
  maxLocks?: number;
  showLocks?: boolean;
  showPickIndicator?: boolean;
  displayMode?:
    | "normal"
    | "correct"
    | "incorrect"
    | "push"
    | "disabled"
    | "no_pick"
    | "in_progress";
  showScores?: boolean;
  homeScore?: number;
  awayScore?: number;
  homeTeamPickers?: string[];
  awayTeamPickers?: string[];
  playerDirectory?: PlayerDirectory;
  homeTeamUsers?: any[];
  awayTeamUsers?: any[];
  showLines?: boolean;
  showScoringLine?: boolean;
  showLiveLine?: boolean;
  showCustomLine?: boolean;
  showFlavorText?: boolean;
  enableEdit?: boolean;
  editableLeagueId?: string;
  pushEnabled?: boolean;
  isLive?: boolean;
  homeTeamPollRank?: TeamPollRank;
  awayTeamPollRank?: TeamPollRank;
  homeTeamRecord?: TeamRecords;
  awayTeamRecord?: TeamRecords;
  currentUserEmail?: string;
  rules?: Rules;
  expanded?: boolean;
  completion?: { completed: number; total: number };
  completionSource?: MatchupCardCompletionSource;
  expansionMode?: MatchupCardExpansionMode;
  expandedHeight?: number | string;
  onExpandedChange?: (open: boolean) => void;
}

const mapDisplayMode = (
  displayMode: GameCardProps["displayMode"],
  isLive?: boolean,
): MatchupCardState | undefined => {
  if (isLive || displayMode === "in_progress") return "live";
  if (displayMode === "correct") return "correct";
  if (displayMode === "incorrect") return "incorrect";
  if (displayMode === "push") return "push";
  if (displayMode === "no_pick") return "no-pick";
  if (displayMode === "disabled") return "game-locked";
  return undefined;
};

export const validateAdminLine = (
  value: number,
  pushEnabled: boolean,
): string | undefined => {
  if (!Number.isFinite(value) || Math.abs(value) > 100) {
    return "Line must be a finite number between -100 and 100.";
  }
  if (!pushEnabled) {
    const absoluteLine = Math.abs(value);
    const fraction = absoluteLine - Math.floor(absoluteLine);
    if (Math.abs(fraction - 0.5) > 0.000001) {
      return "Lines must use half points when pushes are disabled.";
    }
  }
  return undefined;
};

const GameCard = ({
  game,
  metadata,
  homeTeam,
  awayTeam,
  leagueId,
  year,
  week,
  onMetadataChange,
  onClick,
  onEdit,
  isSelected,
  isGameOfTheWeek,
  onGameOfTheWeekClick,
  showGameOfTheWeekButton = false,
  pickedTeamId,
  onPick,
  isLocked = false,
  onToggleLock,
  canLock = true,
  lockCount = 0,
  maxLocks = 0,
  showLocks = false,
  displayMode = "normal",
  homeScore,
  awayScore,
  homeTeamPickers = [],
  awayTeamPickers = [],
  playerDirectory,
  showLines = false,
  showScoringLine = true,
  showLiveLine = true,
  showCustomLine = false,
  showFlavorText = false,
  enableEdit = false,
  editableLeagueId,
  pushEnabled = true,
  isLive,
  currentUserEmail,
  homeTeamPollRank,
  awayTeamPollRank,
  homeTeamRecord,
  awayTeamRecord,
  rules,
  expanded,
  completion,
  completionSource,
  expansionMode,
  expandedHeight,
  onExpandedChange,
}: GameCardProps) => {
  const { updateGameLine, updateGameFlavor } = useLeague();
  const [editorOpen, setEditorOpen] = useState(false);
  const [lineInput, setLineInput] = useState(
    metadata?.line === undefined ? "" : String(metadata.line),
  );
  const [flavorInput, setFlavorInput] = useState(metadata?.flavorText || "");
  const [savingLine, setSavingLine] = useState(false);
  const [savingFlavor, setSavingFlavor] = useState(false);
  const [lineError, setLineError] = useState<string>();
  const [flavorError, setFlavorError] = useState<string>();
  const [lineSaved, setLineSaved] = useState(false);
  const [flavorSaved, setFlavorSaved] = useState(false);
  const effectiveLeagueId = editableLeagueId || leagueId;

  const openEditor = () => {
    setLineInput(metadata?.line === undefined ? "" : String(metadata.line));
    setFlavorInput(metadata?.flavorText || "");
    setLineError(undefined);
    setFlavorError(undefined);
    setLineSaved(false);
    setFlavorSaved(false);
    setEditorOpen(true);
  };

  const closeEditor = () => {
    if (!savingLine && !savingFlavor) setEditorOpen(false);
  };

  const saveLine = async () => {
    const line = Number(lineInput);
    const validationError =
      lineInput.trim() === ""
        ? "Enter a scoring line."
        : validateAdminLine(line, pushEnabled);
    if (validationError) {
      setLineError(validationError);
      setLineSaved(false);
      return;
    }
    if (!effectiveLeagueId) {
      setLineError("League context is unavailable. Refresh and try again.");
      return;
    }

    setSavingLine(true);
    setLineError(undefined);
    setLineSaved(false);
    try {
      const result = await updateGameLine(
        effectiveLeagueId,
        String(game.id),
        line,
        year,
        week,
      );
      if (result instanceof Error) throw result;
      onMetadataChange?.({ id: game.id, line });
      setLineSaved(true);
    } catch (error) {
      setLineError(
        error instanceof Error ? error.message : "Failed to save scoring line.",
      );
    } finally {
      setSavingLine(false);
    }
  };

  const saveFlavor = async () => {
    if (!effectiveLeagueId) {
      setFlavorError("League context is unavailable. Refresh and try again.");
      return;
    }
    if (Array.from(flavorInput).length > 1500) {
      setFlavorError("Matchup story must be 1,500 characters or fewer.");
      return;
    }

    setSavingFlavor(true);
    setFlavorError(undefined);
    setFlavorSaved(false);
    try {
      const result = await updateGameFlavor(
        effectiveLeagueId,
        String(game.id),
        flavorInput,
        year,
        week,
      );
      if (result instanceof Error) throw result;
      onMetadataChange?.({ id: game.id, flavorText: flavorInput });
      setFlavorSaved(true);
    } catch (error) {
      setFlavorError(
        error instanceof Error ? error.message : "Failed to save matchup story.",
      );
    } finally {
      setSavingFlavor(false);
    }
  };

  const adaptedGame: Game = {
    ...game,
    homePoints: homeScore ?? game.homePoints,
    awayPoints: awayScore ?? game.awayPoints,
  };

  const props: MatchupCardProps = {
    game: adaptedGame,
    metadata,
    homeTeam,
    awayTeam,
    state: mapDisplayMode(displayMode, isLive),
    pickedTeamId,
    onPick,
    gameOfTheWeek: Boolean(isGameOfTheWeek),
    onGameOfTheWeekClick,
    showGameOfTheWeekButton,
    currentUserLocked: isLocked,
    onToggleLock,
    canLock,
    lockCount,
    maxLocks,
    showLocks,
    gameLocked: displayMode === "disabled",
    revealSelections: Boolean(
      isLive ||
        game.completed ||
        displayMode === "correct" ||
        displayMode === "incorrect" ||
        displayMode === "push",
    ),
    completion:
      completion ??
      (homeTeamPickers.length + awayTeamPickers.length > 0
        ? {
            completed: homeTeamPickers.length + awayTeamPickers.length,
            total: homeTeamPickers.length + awayTeamPickers.length,
          }
        : undefined),
    completionSource,
    homePickers: homeTeamPickers,
    awayPickers: awayTeamPickers,
    playerDirectory,
    currentUserEmail,
    rules,
    homeTeamPollRank,
    awayTeamPollRank,
    homeTeamRecord,
    awayTeamRecord,
    showFlavorText,
    showLines,
    showScoringLine,
    showLiveLine,
    selectedForAdmin: Boolean(isSelected && !pickedTeamId),
    onCardClick: onClick,
    onEdit: onEdit || (enableEdit ? openEditor : undefined),
    expanded,
    onExpandedChange,
    expansionMode,
    expandedHeight,
  };

  return (
    <>
      <MatchupCard {...props} />
      <Dialog
        open={editorOpen}
        onClose={closeEditor}
        maxWidth="sm"
        fullWidth
        aria-labelledby={"game-metadata-editor-" + game.id}
      >
        <DialogTitle id={"game-metadata-editor-" + game.id}>
          Edit {awayTeam.school} at {homeTeam.school}
        </DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              The scoring line and matchup story save independently. Changes
              remain available until this game kicks off.
            </Typography>

            {showCustomLine && (
              <Stack spacing={1}>
                <TextField
                  label="Scoring line"
                  type="number"
                  value={lineInput}
                  onChange={(event) => {
                    setLineInput(event.target.value);
                    setLineError(undefined);
                    setLineSaved(false);
                  }}
                  inputProps={{ min: -100, max: 100, step: 0.5 }}
                  error={Boolean(lineError)}
                  helperText={
                    lineError ||
                    (pushEnabled
                      ? "Whole or half-point lines are allowed."
                      : "A half-point hook is required when pushes are disabled.")
                  }
                  disabled={savingLine}
                  fullWidth
                />
                {lineSaved && (
                  <Alert severity="success">Scoring line saved.</Alert>
                )}
                <Button
                  variant="contained"
                  onClick={saveLine}
                  disabled={savingLine || !effectiveLeagueId}
                  sx={{ alignSelf: "flex-end" }}
                >
                  {savingLine ? "Saving line..." : "Save scoring line"}
                </Button>
              </Stack>
            )}

            {showFlavorText && (
              <Stack spacing={1}>
                <TextField
                  label="Matchup story / flavor text"
                  value={flavorInput}
                  onChange={(event) => {
                    setFlavorInput(event.target.value);
                    setFlavorError(undefined);
                    setFlavorSaved(false);
                  }}
                  multiline
                  minRows={4}
                  maxRows={10}
                  inputProps={{ maxLength: 1500 }}
                  error={Boolean(flavorError)}
                  helperText={
                    flavorError ||
                    Array.from(flavorInput).length.toLocaleString() + " / 1,500"
                  }
                  disabled={savingFlavor}
                  fullWidth
                />
                {flavorSaved && (
                  <Alert severity="success">Matchup story saved.</Alert>
                )}
                <Button
                  variant="contained"
                  onClick={saveFlavor}
                  disabled={savingFlavor || !effectiveLeagueId}
                  sx={{ alignSelf: "flex-end" }}
                >
                  {savingFlavor ? "Saving story..." : "Save matchup story"}
                </Button>
              </Stack>
            )}

            {!showCustomLine && !showFlavorText && (
              <Alert severity="info">
                Custom lines and matchup stories are disabled in league rules.
              </Alert>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeEditor} disabled={savingLine || savingFlavor}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default GameCard;
export type { GameCardProps };
